import { apiClient } from './api';

export interface Wallet {
    id: string;
    user_id: string;
    type: 'personal' | 'company' | 'system';
    currency: string;
    balance: string; // Decimal returned as string
    is_frozen: boolean;
    created_at: string;
}

export interface Transaction {
    id: string;
    type: string;
    status: string;
    amount: string;
    currency: string;
    created_at: string;
    description?: string;
    balance_after?: string;
}

class FinancialService {

    async getMyWallets(): Promise<Wallet[]> {
        const result = await apiClient.financialServiceRequest('/wallets', {
            method: 'GET'
        });
        return result || [];
    }

    async createWallet(currency: string, type: 'personal' | 'company' = 'personal'): Promise<Wallet> {
        return apiClient.financialServiceRequest('/wallets', {
            method: 'POST',
            body: JSON.stringify({ currency, type })
        });
    }

    async sendTransfer(data: { receiverUserId: string; amount: number; currency: string; description?: string }) {
        return apiClient.financialServiceRequest('/transactions/transfer/internal', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async getTransactions(): Promise<any[]> {
        const result = await apiClient.financialServiceRequest('/transactions', {
            method: 'GET'
        });
        return result || [];
    }

    async addFunds(data: { amount: number; currency: string; provider: string; paymentMethod?: string; reference?: string }) {
        return apiClient.financialServiceRequest('/payments/deposit', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    // Lookup user by email - uses User Service directly
    async lookupUser(email: string) {
        return apiClient.userServiceRequest(`/users/lookup?email=${encodeURIComponent(email)}`);
    }

    // =========== REQUEST PAYMENT ===========
    async createPaymentRequest(data: { payerEmail: string; amount: number; currency: string; description?: string; dueDate?: string }) {
        return apiClient.financialServiceRequest('/payment-requests', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async getPaymentRequests(): Promise<any[]> {
        const result = await apiClient.financialServiceRequest('/payment-requests', {
            method: 'GET'
        });
        return result || [];
    }

    // =========== INVOICING ===========
    async createInvoice(data: { clientEmail: string; items: any[]; totalAmount: number; currency: string; dueDate?: string; notes?: string }) {
        return apiClient.financialServiceRequest('/invoices', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async getInvoices(): Promise<any[]> {
        const result = await apiClient.financialServiceRequest('/invoices', {
            method: 'GET'
        });
        return result || [];
    }

    // =========== ESCROW ===========
    async createEscrow(data: { counterpartyEmail: string; amount: number; currency: string; milestones: any[]; description?: string }) {
        return apiClient.financialServiceRequest('/escrow', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async getEscrows(): Promise<any[]> {
        const result = await apiClient.financialServiceRequest('/escrow', {
            method: 'GET'
        });
        return result || [];
    }

    async releaseMilestone(escrowId: string, milestoneId: string) {
        return apiClient.financialServiceRequest(`/escrow/${escrowId}/milestones/${milestoneId}/release`, {
            method: 'POST'
        });
    }

    // =========== FINANCIAL SUMMARY ===========
    async getFinancialSummary(): Promise<{ totalIncome: number; totalExpenses: number; netBalance: number; currency: string }> {
        const result = await apiClient.financialServiceRequest('/summary', { method: 'GET' });
        return result || { totalIncome: 0, totalExpenses: 0, netBalance: 0, currency: 'USD' };
    }

    async getIncomeBreakdown(): Promise<{
        total: number;
        byRealm: { realm: string; amount: number; percentage: number }[];
        growth: { month: string; amount: number }[];
        currency: string;
    }> {
        const result = await apiClient.financialServiceRequest('/income/breakdown', { method: 'GET' });
        // Fallback/stub data for initial development if backend endpoint is cold
        return result || {
            total: 12500,
            currency: 'USD',
            byRealm: [
                { realm: 'Freelance', amount: 8000, percentage: 64 },
                { realm: 'Local Gigs', amount: 3000, percentage: 24 },
                { realm: 'Employment', amount: 1500, percentage: 12 }
            ],
            growth: [
                { month: 'Jan', amount: 1200 },
                { month: 'Feb', amount: 1800 },
                { month: 'Mar', amount: 2200 },
                { month: 'Apr', amount: 2500 }
            ]
        };
    }

    // =========== WITHDRAWAL ===========
    async requestWithdrawal(data: { amount: number; currency: string; method: string; provider: string; details: any }) {
        return apiClient.financialServiceRequest('/withdrawals', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    // =========== KYC ===========
    async uploadKycDocument(file: File) {
        const formData = new FormData();
        formData.append('document', file);
        return apiClient.financialServiceRequest('/kyc/upload', {
            method: 'POST',
            body: formData
        });
    }

    async submitVerification(data: { level: string; notes?: string; documents?: { type: string; url: string }[] }) {
        return apiClient.financialServiceRequest('/kyc/submit', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async getVerificationStatus() {
        // Return default structure on error/null
        try {
            const result = await apiClient.financialServiceRequest('/kyc/status', {
                method: 'GET'
            });
            return result || { status: 'unverified', level: 'unverified' };
        } catch (error) {
            return { status: 'unverified', level: 'unverified' };
        }
    }

    // =========== ADMIN ===========
    async reconcile(date?: string) {
        return apiClient.financialServiceRequest('/admin/reconcile', {
            method: 'POST',
            body: JSON.stringify({ date })
        });
    }

    async getPendingVerifications() {
        const result = await apiClient.financialServiceRequest('/admin/verifications', { method: 'GET' });
        return result || [];
    }

    async adminReviewVerification(data: { userId: string; action: 'approve' | 'reject'; level?: string; reason?: string }) {
        return apiClient.financialServiceRequest('/admin/verification-review', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }
}

export default new FinancialService();
